import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import { Eraser } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Mobile-friendly signature pad backed by an HTML5 canvas.
 * - Captures via pointer events (works for touch + stylus + mouse).
 * - Exports a trimmed PNG data URL via the `getDataUrl()` imperative handle.
 * - Auto-sizes canvas to its container with a 2x device-pixel multiplier
 *   so signatures look crisp on retina screens.
 */
const SignaturePad = forwardRef(function SignaturePad(
  { onChange, height = 180, label, testId },
  ref,
) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef(null);
  const dirtyRef = useRef(false);
  const [isEmpty, setIsEmpty] = useState(true);

  // Setup canvas resolution
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      const ratio = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * ratio;
      canvas.height = rect.height * ratio;
      const ctx = canvas.getContext('2d');
      ctx.scale(ratio, ratio);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = '#0d1829';
      ctx.lineWidth = 2.2;
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  const getPoint = (evt) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: evt.clientX - rect.left, y: evt.clientY - rect.top };
  };

  const start = (evt) => {
    evt.preventDefault();
    drawingRef.current = true;
    lastRef.current = getPoint(evt);
    canvasRef.current.setPointerCapture?.(evt.pointerId);
  };

  const move = (evt) => {
    if (!drawingRef.current) return;
    evt.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const p = getPoint(evt);
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
    if (!dirtyRef.current) {
      dirtyRef.current = true;
      setIsEmpty(false);
      onChange?.(true);
    }
  };

  const end = (evt) => {
    drawingRef.current = false;
    lastRef.current = null;
    try {
      if (evt?.pointerId != null) canvasRef.current?.releasePointerCapture?.(evt.pointerId);
    } catch {}
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
    dirtyRef.current = false;
    setIsEmpty(true);
    onChange?.(false);
  };

  useImperativeHandle(ref, () => ({
    isEmpty: () => !dirtyRef.current,
    clear,
    getDataUrl: () => (dirtyRef.current ? canvasRef.current.toDataURL('image/png') : null),
  }));

  return (
    <div className="space-y-2">
      {label && (
        <div className="flex items-center justify-between">
          <label className="text-xs uppercase tracking-wider text-slate-400">{label}</label>
          <button
            type="button"
            onClick={clear}
            className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1"
            data-testid={testId ? `${testId}-clear` : 'signature-clear'}
          >
            <Eraser className="w-3 h-3" /> Clear
          </button>
        </div>
      )}
      <div className="relative rounded-lg border border-white/10 bg-white">
        <canvas
          ref={canvasRef}
          style={{ width: '100%', height: `${height}px`, touchAction: 'none' }}
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerCancel={end}
          onPointerLeave={end}
          data-testid={testId || 'signature-canvas'}
        />
        {isEmpty && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-slate-400 text-sm">
            Sign here with finger or stylus
          </div>
        )}
      </div>
    </div>
  );
});

export default SignaturePad;
